/**
 * TrainingsTab - Onglet Formations
 * Gestion des formations des employés
 */

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { ScrollArea } from "../../ui/scroll-area";
import { Checkbox } from "../../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import { 
  Plus, GraduationCap, Calendar, MapPin, Building, Clock,
  Euro, Award, Loader2, AlertCircle, Trash2, Edit2, CheckCircle
} from "lucide-react";
import { DatePickerFr, formatDateFr } from "../../ui/date-picker-fr";
import {
  TRAINING_TYPE_OPTIONS,
  TRAINING_STATUS_OPTIONS,
  getTrainingTypeLabel,
  getTrainingStatusLabel,
  getTrainingStatusColor,
} from "../../../lib/hr-constants";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TrainingsTab({ employee }) {
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [form, setForm] = useState({
    title: "",
    training_type: "",
    provider: "",
    start_date: "",
    end_date: "",
    duration_hours: "",
    cost: "",
    location: "",
    description: "",
    is_mandatory: false,
  });
  
  const [editForm, setEditForm] = useState({});

  const token = localStorage.getItem("token");

  const fetchTrainings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/api/hr/employees/${employee.id}/trainings`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTrainings(response.data.trainings || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [employee.id, token]);

  useEffect(() => {
    fetchTrainings();
  }, [fetchTrainings]);

  const handleAdd = async () => {
    if (!form.title || !form.training_type || !form.start_date) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        duration_hours: form.duration_hours ? parseFloat(form.duration_hours) : null,
        cost: form.cost ? parseFloat(form.cost) : null,
      };
      await axios.post(
        `${API}/api/hr/employees/${employee.id}/trainings`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowAddDialog(false);
      setForm({ title: "", training_type: "", provider: "", start_date: "", end_date: "", duration_hours: "", cost: "", location: "", description: "", is_mandatory: false });
      fetchTrainings();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedTraining) return;
    setSaving(true);
    try {
      const payload = { ...editForm };
      if (payload.duration_hours) payload.duration_hours = parseFloat(payload.duration_hours);
      if (payload.cost) payload.cost = parseFloat(payload.cost);
      await axios.put(
        `${API}/api/hr/employees/${employee.id}/trainings/${selectedTraining.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowEditDialog(false);
      fetchTrainings();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTraining) return;
    setSaving(true);
    try {
      await axios.delete(
        `${API}/api/hr/employees/${employee.id}/trainings/${selectedTraining.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowDeleteDialog(false);
      fetchTrainings();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (t) => {
    setSelectedTraining(t);
    setEditForm({
      title: t.title || "",
      training_type: t.training_type || "",
      provider: t.provider || "",
      start_date: t.start_date || "",
      end_date: t.end_date || "",
      duration_hours: t.duration_hours?.toString() || "",
      cost: t.cost?.toString() || "",
      location: t.location || "",
      description: t.description || "",
      status: t.status || "",
      completion_date: t.completion_date || "",
      certificate_obtained: t.certificate_obtained || false,
      notes: t.notes || "",
    });
    setShowEditDialog(true);
  };

  const planned = trainings.filter(t => t.status === "planned" || t.status === "in_progress");
  const completed = trainings.filter(t => t.status === "completed" || t.status === "cancelled");

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <GraduationCap className="w-5 h-5 text-indigo-600" />
          <h3 className="text-lg font-semibold text-slate-900">Formations</h3>
          <Badge variant="secondary">{trainings.length}</Badge>
        </div>
        <Button onClick={() => setShowAddDialog(true)} data-testid="add-training-btn">
          <Plus className="w-4 h-4 mr-2" />
          Ajouter une formation
        </Button>
      </div>

      {planned.length > 0 && (
        <Card className="border-indigo-200 bg-indigo-50/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4 text-indigo-600" />
              En cours / À venir ({planned.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            {planned.map(t => (
              <TrainingCard key={t.id} training={t} onEdit={() => openEdit(t)} onDelete={() => { setSelectedTraining(t); setShowDeleteDialog(true); }} />
            ))}
          </CardContent>
        </Card>
      )}

      {completed.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              Historique ({completed.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ScrollArea className="max-h-[300px]">
              <div className="space-y-3">
                {completed.map(t => (
                  <TrainingCard key={t.id} training={t} onEdit={() => openEdit(t)} onDelete={() => { setSelectedTraining(t); setShowDeleteDialog(true); }} />
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {trainings.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <GraduationCap className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-1">Aucune formation</h3>
            <p className="text-sm text-slate-500 mb-4">Ajoutez une formation pour cet employé</p>
            <Button onClick={() => setShowAddDialog(true)}><Plus className="w-4 h-4 mr-2" />Ajouter</Button>
          </CardContent>
        </Card>
      )}

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Nouvelle formation</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Intitulé *</Label>
              <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Nom de la formation" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type *</Label>
                <Select value={form.training_type} onValueChange={(v) => setForm({...form, training_type: v})}>
                  <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                  <SelectContent>
                    {TRAINING_TYPE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Organisme</Label>
                <Input value={form.provider} onChange={(e) => setForm({...form, provider: e.target.value})} placeholder="Organisme de formation" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date début *</Label>
                <DatePickerFr value={form.start_date} onChange={(v) => setForm({...form, start_date: v})} />
              </div>
              <div className="space-y-2">
                <Label>Date fin</Label>
                <DatePickerFr value={form.end_date} onChange={(v) => setForm({...form, end_date: v})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Durée (heures)</Label>
                <Input type="number" value={form.duration_hours} onChange={(e) => setForm({...form, duration_hours: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Coût (€)</Label>
                <Input type="number" value={form.cost} onChange={(e) => setForm({...form, cost: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Lieu</Label>
              <Input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="mandatory" checked={form.is_mandatory} onCheckedChange={(v) => setForm({...form, is_mandatory: v})} />
              <label htmlFor="mandatory" className="text-sm">Formation obligatoire</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Annuler</Button>
            <Button onClick={handleAdd} disabled={saving || !form.title || !form.training_type || !form.start_date}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Modifier la formation</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Intitulé</Label>
              <Input value={editForm.title} onChange={(e) => setEditForm({...editForm, title: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={editForm.training_type} onValueChange={(v) => setEditForm({...editForm, training_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TRAINING_TYPE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Statut</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm({...editForm, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TRAINING_STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date début</Label>
                <DatePickerFr value={editForm.start_date} onChange={(v) => setEditForm({...editForm, start_date: v})} />
              </div>
              <div className="space-y-2">
                <Label>Date fin</Label>
                <DatePickerFr value={editForm.end_date} onChange={(v) => setEditForm({...editForm, end_date: v})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Date de réalisation</Label>
              <DatePickerFr value={editForm.completion_date} onChange={(v) => setEditForm({...editForm, completion_date: v})} />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="cert" checked={editForm.certificate_obtained} onCheckedChange={(v) => setEditForm({...editForm, certificate_obtained: v})} />
              <label htmlFor="cert" className="text-sm">Certificat obtenu</label>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={editForm.notes} onChange={(e) => setEditForm({...editForm, notes: e.target.value})} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Annuler</Button>
            <Button onClick={handleEdit} disabled={saving}>{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette formation ?</AlertDialogTitle>
            <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function TrainingCard({ training, onEdit, onDelete }) {
  return (
    <div className="flex items-start gap-4 p-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors group">
      <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
        <GraduationCap className="w-5 h-5 text-indigo-600" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="font-medium text-slate-900">{training.title}</span>
          <Badge variant="outline" className={getTrainingStatusColor(training.status)}>{getTrainingStatusLabel(training.status)}</Badge>
          {training.is_mandatory && <Badge className="bg-red-100 text-red-700">Obligatoire</Badge>}
          {training.certificate_obtained && <Badge className="bg-green-100 text-green-700"><Award className="w-3 h-3 mr-1" />Certifié</Badge>}
        </div>
        <div className="flex flex-wrap gap-x-4 text-sm text-slate-500">
          <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{formatDateFr(training.start_date)}{training.end_date && ` → ${formatDateFr(training.end_date)}`}</span>
          {training.provider && <span className="flex items-center gap-1"><Building className="w-3.5 h-3.5" />{training.provider}</span>}
          {training.duration_hours && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{training.duration_hours}h</span>}
          {training.cost && <span className="flex items-center gap-1"><Euro className="w-3.5 h-3.5" />{training.cost.toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })} €</span>}
        </div>
      </div>
      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit}><Edit2 className="w-4 h-4" /></Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={onDelete}><Trash2 className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}
